import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useCapabilities } from "@/lib/capabilities";
import { TopBar } from "@/components/layout/TopBar";
import { CheckCircle2, ArrowRight, ExternalLink, Sparkles, ShieldCheck, Loader2 } from "lucide-react";

/**
 * ELIA · /app/finance/success — post-checkout landing.
 * Waits for the Stripe webhook to promote user.plan_id, then displays the
 * activated plan with its unlocked entitlements/features.
 */
export default function FinanceSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const navigate = useNavigate();
  const { user, refresh: refreshAuth } = useAuth();
  const { refresh: refreshCaps } = useCapabilities();
  const [session, setSession] = useState(null);
  const [portalLoading, setPortalLoading] = useState(false);
  const [webhookConfirmed, setWebhookConfirmed] = useState(false);
  const [waitingWebhook, setWaitingWebhook] = useState(true);

  useEffect(() => {
    if (!sessionId) { navigate("/app"); return; }
    api.get(`/finance/checkout/elia/session/${sessionId}`)
      .then((r) => setSession(r.data))
      .catch(() => { setSession({ error: true }); });
  }, [sessionId, navigate]);

  useEffect(() => {
    if (!session?.plan_id || webhookConfirmed) return;
    let cancelled = false;
    let attempts = 0;
    const check = async () => {
      if (cancelled || attempts >= 15) { setWaitingWebhook(false); return; }
      attempts++;
      try {
        const { data } = await api.get("/auth/me");
        if (data.plan_id === session.plan_id) {
          setWebhookConfirmed(true);
          setWaitingWebhook(false);
          if (refreshAuth) refreshAuth();
          if (refreshCaps) refreshCaps();
          return;
        }
      } catch { /* silent */ }
      setTimeout(check, 2000);
    };
    check();
    return () => { cancelled = true; };
  }, [session, webhookConfirmed, refreshAuth, refreshCaps]);

  const openPortal = async () => {
    setPortalLoading(true);
    try {
      const { data } = await api.post("/finance/portal", { return_url: `${window.location.origin}/app` });
      if (data?.portal_url) window.location.href = data.portal_url;
    } catch {
      window.open("mailto:finance@altioracoop.com?subject=Gérer mon abonnement ALTIORA", "_self");
    } finally { setPortalLoading(false); }
  };

  if (!session) {
    return (
      <div className="min-h-screen bg-[#F8F7F3] flex items-center justify-center">
        <Loader2 className="h-4 w-4 animate-spin text-[#0B2545]" strokeWidth={1.5} />
      </div>
    );
  }

  const p = session.plan;
  const amount = (session.amount_total || 0) / 100;
  const currency = (session.currency || "cad").toUpperCase();

  return (
    <div className="min-h-screen bg-[#F8F7F3]">
      <TopBar />
      <main className="max-w-4xl mx-auto px-6 md:px-10 py-12 md:py-20" data-testid="finance-success-page">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] } }}
        >
          <div className="inline-flex items-center gap-2 mb-6" data-testid="finance-success-badge">
            <div className="h-8 w-8 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center">
              <CheckCircle2 className="h-4 w-4" strokeWidth={2} />
            </div>
            <span className="text-[10px] font-mono uppercase tracking-[0.28em] text-emerald-700">
              Paiement confirmé · Bienvenue chez ALTIORA
            </span>
          </div>

          <h1 className="font-display text-4xl md:text-6xl text-[#1F2937] tracking-tight leading-[1.05]">
            Merci{user?.first_name ? `, ${user.first_name}` : ""}.
            <br />
            <span className="text-[#5B5B62]">Votre {p?.name_fr || "abonnement"} est actif.</span>
          </h1>

          {p?.tagline_fr && (
            <p className="mt-4 text-lg text-[#7A5B14] italic max-w-2xl leading-relaxed">
              « {p.tagline_fr} »
            </p>
          )}

          {amount > 0 && (
            <div className="mt-8 font-mono text-xs uppercase tracking-widest text-[#5B5B62]">
              {amount.toLocaleString()} {currency}/mois · Facture disponible dans votre espace abonnement
            </div>
          )}
        </motion.div>

        <div className="mt-10">
          {waitingWebhook && !webhookConfirmed && (
            <div className="rounded-lg border border-[#C5A880]/40 bg-[#FBF6EA] px-4 py-3 flex items-center gap-3" data-testid="finance-success-waiting">
              <Loader2 className="h-4 w-4 animate-spin text-[#7A5B14]" strokeWidth={1.5} />
              <div className="text-sm text-[#7A5B14]">
                Activation en cours des nouvelles fonctionnalités… (quelques secondes)
              </div>
            </div>
          )}
          {webhookConfirmed && (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 flex items-center gap-3" data-testid="finance-success-active">
              <CheckCircle2 className="h-4 w-4 text-emerald-700" strokeWidth={2} />
              <div className="text-sm text-emerald-800">
                Vos fonctionnalités sont désormais actives. Bonne exploration.
              </div>
            </div>
          )}
        </div>

        {p?.features_fr?.length > 0 && (
          <section className="mt-12" data-testid="finance-success-features">
            <div className="text-[10px] font-mono uppercase tracking-[0.28em] text-[#0B2545]">
              Ce que vous venez de débloquer
            </div>
            <h2 className="mt-2 font-display text-2xl md:text-3xl text-[#1F2937] tracking-tight">
              Vos nouvelles capacités
            </h2>
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-3">
              {p.features_fr.map((f, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0, transition: { delay: 0.05 * i } }}
                  className="flex items-start gap-3 rounded-lg border border-[#E4E1D8] bg-white p-4"
                >
                  <div className="h-8 w-8 rounded-md bg-emerald-100 text-emerald-700 flex items-center justify-center flex-shrink-0">
                    <Sparkles className="h-4 w-4" strokeWidth={1.8} />
                  </div>
                  <div className="text-sm text-[#1F2937] leading-relaxed">{f}</div>
                </motion.div>
              ))}
            </div>
          </section>
        )}

        {p?.entitlements?.length > 0 && (
          <section className="mt-10" data-testid="finance-success-entitlements">
            <div className="text-[10px] font-mono uppercase tracking-[0.28em] text-[#7A5B14]">
              Habilitations techniques accordées
            </div>
            <div className="mt-3 flex flex-wrap gap-1.5">
              {p.entitlements.map((e) => (
                <span key={e} className="inline-block text-[10px] font-mono bg-[#F1EFE8] text-[#5B5B62] rounded-full px-2 py-0.5">
                  {e}
                </span>
              ))}
            </div>
          </section>
        )}

        <div className="mt-14 flex flex-wrap items-center gap-3">
          <Link
            to="/app"
            data-testid="finance-success-go-dashboard"
            className="inline-flex items-center gap-2 rounded-md bg-[#0B2545] hover:bg-[#123163] text-white px-5 py-3 text-sm font-medium transition-colors"
          >
            Retour au tableau de bord <ArrowRight className="h-4 w-4" strokeWidth={2} />
          </Link>
          <button
            onClick={openPortal}
            disabled={portalLoading}
            data-testid="finance-success-manage-subscription"
            className="inline-flex items-center gap-2 rounded-md border border-[#E4E1D8] hover:border-[#0B2545] text-[#1F2937] px-5 py-3 text-sm font-medium transition-colors disabled:opacity-60"
          >
            {portalLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={2} /> : <ExternalLink className="h-3.5 w-3.5" strokeWidth={2} />}
            Gérer mon abonnement
          </button>
        </div>

        <div className="mt-16 pt-8 border-t border-[#E4E1D8] flex items-start gap-3">
          <ShieldCheck className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" strokeWidth={1.8} />
          <p className="text-xs text-[#5B5B62] leading-relaxed max-w-2xl">
            Résiliation en un clic à tout moment. Aucune reconduction cachée. Votre coffre
            documentaire et vos dossiers restent accessibles même si vous revenez au plan
            gratuit — ALTIORA ne verrouille jamais vos données.
          </p>
        </div>
      </main>
    </div>
  );
}
